import React, { useState, useMemo } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material"
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as PowerIcon,
  Clear as ClearIcon,
} from "@mui/icons-material"
import { deleteExpert, toggleExpertStatus } from "services/expert"
import { verifyResponse } from "utils/httpRequest"

const ExpertTable = ({
  experts,
  access,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const [searchForm, setSearchForm] = useState({
    keyword: "",
    isActive: "",
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [expertToDelete, setExpertToDelete] = useState(null)

  // Client-side filtering
  const filteredExperts = useMemo(() => {
    return experts.filter((expert) => {
      const matchesKeyword = !searchForm.keyword || 
        expert.name?.toLowerCase().includes(searchForm.keyword.toLowerCase()) ||
        expert.description?.toLowerCase().includes(searchForm.keyword.toLowerCase())
      
      const matchesStatus = !searchForm.isActive || 
        (searchForm.isActive === "1" && expert.isActive) ||
        (searchForm.isActive === "0" && !expert.isActive)
      
      return matchesKeyword && matchesStatus
    })
  }, [experts, searchForm])

  const handleSearchChange = (field, value) => {
    setSearchForm({ ...searchForm, [field]: value })
  }

  const handleClearSearch = () => {
    setSearchForm({
      keyword: "",
      isActive: "",
    })
  }

  const handleDeleteClick = (expert) => {
    setExpertToDelete(expert)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (expertToDelete) {
      try {
        const response = await deleteExpert(expertToDelete.id)
        verifyResponse(response)
        onDelete()
        setDeleteDialogOpen(false)
        setExpertToDelete(null)
      } catch (error) {
        console.error("Error deleting expert:", error)
      }
    }
  }

  const handleToggleStatus = async (expert) => {
    try {
      const response = await toggleExpertStatus(expert.id)
      verifyResponse(response)
      onToggleStatus()
    } catch (error) {
      console.error("Error toggling expert status:", error)
    }
  }

  return (
    <div>
      <div className="search-section">
        <div className="search-form">
          <TextField
            size="small"
            placeholder="Search by name or description..."
            value={searchForm.keyword}
            onChange={(e) => handleSearchChange("keyword", e.target.value)}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={searchForm.isActive}
              onChange={(e) => handleSearchChange("isActive", e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            className="clear-search-btn"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearSearch}
            size="small"
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="table-container">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sort Order</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExperts.length === 0 && (
                <TableRow>
                  <TableCell className="table-empty" colSpan={5}>
                    {experts.length === 0
                      ? "No experts found."
                      : "No experts match your search criteria."}
                  </TableCell>
                </TableRow>
              )}
              {filteredExperts.map((expert) => (
                <TableRow key={expert.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {expert.name}
                    </Typography>
                  </TableCell>
                  <TableCell className="description-cell">
                    {expert.description}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expert.isActive ? "Active" : "Inactive"}
                      className={`status-badge ${
                        expert.isActive ? "status-active" : "status-inactive"
                      }`}
                    />
                  </TableCell>
                  <TableCell>{expert.sortOrder ?? 0}</TableCell>
                  <TableCell>
                    <div className="action-buttons">
                      {access?.editExpertAccess && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(expert)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {access?.toggleStatusExpertAccess && (
                        <Tooltip title="Toggle Status">
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(expert)}
                          >
                            <PowerIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {access?.deleteExpertAccess && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(expert)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the Expert "{expertToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ExpertTable

